import { initPylon } from '@findigs/helpers/src/integrations/pylon/pylon';
import { NavLink as RemixNavLink, useLocation } from '@remix-run/react';
import { useEffect, useMemo } from 'react';
import type { NavbarPureProps } from '~/components/navbar-pure';
import { NavbarPure } from '~/components/navbar-pure';
import { useBackButtonDestination } from '~/hooks/use-back-button-destination';
import type { LoaderResult } from './navbar.server';
import ServicesIcon from '@findigs/ui-kit/src/icons/material/dashboard_customize.filled.inline.svg';
import DashboardIcon from '@findigs/ui-kit/src/icons/material/home.filled.inline.svg';

const primaryLinks: NavbarPureProps<any>['primaryLinks'] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: <DashboardIcon width="20" height="20" aria-hidden={true} />,
    active: false,
  },
  {
    title: 'Services',
    href: '/services',
    icon: <ServicesIcon width="20" height="20" aria-hidden={true} />,
    active: false,
  },
];

const secondaryLinks: NavbarPureProps<any>['secondaryLinks'] = [
  {
    title: 'Get support',
    onClick: () => {
      if (window.Pylon) {
        window.Pylon('show');
      }
    },
    active: false,
  },
];

const tertiaryLinks: NavbarPureProps<any>['tertiaryLinks'] = [
  {
    title: 'Privacy policy',
    href: 'https://www.findigs.com/legal/privacy-policy',
    target: '_blank',
    active: false,
  },
  {
    title: 'Terms of use',
    href: 'https://www.findigs.com/legal/terms-and-conditions',
    target: '_blank',
    active: false,
  },
];

const logoutHref = '/logout';

export type NavbarProps = {
  loaderData: LoaderResult;
} & Pick<NavbarPureProps<any>, 'logoHref' | 'variant'>;

const getUrlPathName = (url: string) => {
  const urlObj = new URL((url.startsWith('/') ? 'http://a' : '') + url);
  return urlObj.pathname;
};

export const Navbar = ({ loaderData, logoHref, variant }: NavbarProps) => {
  const { backButtonDestination } = useBackButtonDestination();

  const location = useLocation();

  useEffect(() => {
    if (loaderData && loaderData.profile) {
      initPylon(
        loaderData.PYLON_CHAT_ENV,
        loaderData.profile,
        loaderData.PYLON_CHAT_ENV.applicantAccountId
      );
    }
  }, [loaderData, location.pathname]);

  const enhancedPrimaryLinks = useMemo(() => {
    if (!loaderData?.profile) return [];
    return primaryLinks.map((link) => {
      const active = Boolean(
        link.href?.startsWith('/') &&
          location.pathname === getUrlPathName(link.href)
      );
      return {
        ...link,
        active,
      };
    });
  }, [loaderData?.profile, location.pathname]);

  return (
    <NavbarPure
      primaryLinks={enhancedPrimaryLinks}
      secondaryLinks={secondaryLinks}
      tertiaryLinks={tertiaryLinks}
      backButtonDestination={backButtonDestination}
      logoHref={logoHref}
      logoutHref={logoutHref}
      frameworkLink={RemixNavLink}
      frameworkLinkProp="to"
      profile={loaderData?.profile}
      variant={variant}
    />
  );
};
